import { AxiosResponse } from 'axios';
import { ON_FARM_ANIMAL_API_ENDPOINTS } from '../constants/apiEndpoints';
import { appContextDelete, appContextGet, appContextPost, appContextPut } from './httpCommon/appAxios.service';
import { CreateOnFarmAnimalRequest, OnFarmAnimal, UpdateOnFarmAnimalRequest } from '../types/interfaces/animal.interfaces';
import { ODataParams, PagedResponse, StandardParams } from '../types/interfaces/apiParams.interfaces';
import { Attachment } from '../types/interfaces';

export const getOnFarmAccountAnimals = (
  params?: StandardParams,
): Promise<AxiosResponse<OnFarmAnimal[] | PagedResponse<OnFarmAnimal>>> => {
  return appContextGet(ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL, params);
};

export const getOnFarmAccountAnimalsOdata = (
  params?: ODataParams,
): Promise<AxiosResponse<OnFarmAnimal[] | PagedResponse<OnFarmAnimal>>> => {
  return appContextGet(ON_FARM_ANIMAL_API_ENDPOINTS.ODATA, params);
};

export const getOnFarmAnimalById = (
  animalId: number | string,
  params?: { include?: string },
): Promise<AxiosResponse<OnFarmAnimal>> => {
  return appContextGet(`${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}`, params);
};

export const getOnFarmAnimalCount = (params?: { filter?: string }): Promise<AxiosResponse<number>> => {
  return appContextGet(ON_FARM_ANIMAL_API_ENDPOINTS.COUNT, params);
};

export const createOnFarmAnimal = (animal: CreateOnFarmAnimalRequest): Promise<AxiosResponse<number>> => {
  return appContextPost(ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL, animal);
};

export const updateOnFarmAnimal = (
  animalId: number | string,
  animal: UpdateOnFarmAnimalRequest,
): Promise<AxiosResponse<OnFarmAnimal>> => {
  return appContextPut(`${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}`, animal);
};

export const uploadOnFarmAnimalAttachment = (
  animalId: number | string,
  attachmentFiles: FormData,
): Promise<AxiosResponse<Attachment[]>> => {
  return appContextPost(
    `${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}${ON_FARM_ANIMAL_API_ENDPOINTS.ATTACHMENTS}`,
    attachmentFiles,
  );
};

export const getOnFarmAnimalAttachments = (animalId: number | string, params?: {}): Promise<AxiosResponse<Attachment[]>> => {
  return appContextGet(
    `${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}${ON_FARM_ANIMAL_API_ENDPOINTS.ATTACHMENTS}`,
    params,
  );
};

export const deleteOnFarmAnimalAttachments = (animalId: number | string, attachmentIds: number[]): Promise<AxiosResponse<{}>> => {
  return appContextDelete(
    `${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}${ON_FARM_ANIMAL_API_ENDPOINTS.ATTACHMENTS}`,
    {},
    attachmentIds,
  );
};

export const deleteOnFarmAnimalAttachment = (animalId: number | string, attachmentId: number): Promise<AxiosResponse<{}>> => {
  return appContextDelete(
    `${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}${ON_FARM_ANIMAL_API_ENDPOINTS.ATTACHMENTS}/${attachmentId}`,
  );
};

export const getOnFarmAnimalAttachmentURL = async (
  animalId: string | number,
  attachmentId: number,
  accessToken: string,
): Promise<string> => {
  if (accessToken.startsWith('blob:') || accessToken.startsWith('data:') || accessToken.startsWith('http')) {
    return accessToken;
  }

  const response = await appContextGet(
    `${ON_FARM_ANIMAL_API_ENDPOINTS.ON_FARM_ANIMAL_}${animalId}${ON_FARM_ANIMAL_API_ENDPOINTS.ATTACHMENTS}/${attachmentId}${ON_FARM_ANIMAL_API_ENDPOINTS.SAS_URL}`,
    {},
    { headers: { 'x-access-token': accessToken } },
  );

  return response.data?.url ?? response.data?.Url ?? '';
};

export const resolveOnFarmAttachments = async (
  animalId: string | number,
  attachments: Attachment[] = [],
): Promise<Attachment[]> => {
  return Promise.all(
    attachments.map(async attachment => ({
      ...attachment,
      accessToken: await getOnFarmAnimalAttachmentURL(animalId, attachment.attachmentId, attachment.accessToken),
    })),
  );
};
